import React, { useCallback, useMemo, useState } from "react";
import { Pencil, Plus, Settings2 } from "lucide-react";
import { listPlans, setPriceActive } from "../../api/billing";
import { LoadState } from "../UserDetails/ui";
import { AddPriceModal, ChangeAmountModal, CreatePlanModal, EditPlanModal } from "./PlanModals";
import { PlanSettingsModal } from "./PlanSettingsModal";
import type { AdminPlan, AdminPrice, ProductLine } from "./types";
import { Chip, ConfirmModal, Toggle, linkButtonClass, money, primaryButtonClass, secondaryButtonClass, useAction, useLoad } from "./ui";

type ModalState =
  | { kind: "create" }
  | { kind: "edit"; plan: AdminPlan }
  | { kind: "addPrice"; plan: AdminPlan }
  | { kind: "amount"; plan: AdminPlan; price: AdminPrice }
  | { kind: "settings"; plan: AdminPlan }
  | null;

const isVariant = (plan: AdminPlan) => plan.basePlanId !== null;

/** Price amounts can be changed only for paid recurring prices of regular plans. */
const canChangeAmount = (plan: AdminPlan, price: AdminPrice) =>
  !isVariant(plan) && plan.category !== "FREE" && price.billingPeriod !== "one_time" && price.isActive;

const PlanCard: React.FC<
  Readonly<{
    plan: AdminPlan;
    baseName: string | null;
    productLine: ProductLine;
    busy: boolean;
    onModal: (m: ModalState) => void;
    onTogglePrice: (price: AdminPrice) => void;
  }>
> = ({ plan, baseName, productLine, busy, onModal, onTogglePrice }) => {
  const isDrawpost = productLine === "DRAWPOST_ADS";
  const variant = isVariant(plan);
  return (
    <div className={`rounded-xl border bg-white shadow-sm ${plan.isActive ? "border-gray-200" : "border-dashed border-gray-300 opacity-80"}`}>
      <div className="flex flex-col gap-3 border-b border-gray-100 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-900">{plan.name}</h3>
            <span className="text-xs text-gray-400">#{plan.id}</span>
            <Chip tone="violet">{plan.category}</Chip>
            <Chip tone={plan.visibility === "PUBLIC" ? "emerald" : "amber"}>{plan.visibility.toLowerCase()}</Chip>
            {plan.accountType !== "ALL" && <Chip>{plan.accountType.toLowerCase()} only</Chip>}
            {!plan.isActive && <Chip tone="red">off</Chip>}
            {variant && (
              <Chip tone="amber">
                offer price of {baseName ?? `#${plan.basePlanId}`}
              </Chip>
            )}
          </div>
          {plan.description && <p className="truncate text-xs text-gray-500">{plan.description}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className={secondaryButtonClass} onClick={() => onModal({ kind: "edit", plan })}>
            <Pencil className="w-4 h-4" /> Edit
          </button>
          {!variant && (
            <button type="button" className={secondaryButtonClass} onClick={() => onModal({ kind: "addPrice", plan })}>
              <Plus className="w-4 h-4" /> Price
            </button>
          )}
          {isDrawpost && !variant && (
            <button type="button" className={secondaryButtonClass} onClick={() => onModal({ kind: "settings", plan })}>
              <Settings2 className="w-4 h-4" /> Limits
            </button>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-left">
              {["Period", "Amount", "Credits", "Trial", "Subscribers", "Active", ""].map((h) => (
                <th key={h} className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {plan.prices.map((price) => (
              <tr key={price.id} className={`border-b border-gray-50 last:border-0 ${price.isActive ? "" : "text-gray-400"}`}>
                <td className="px-4 py-2 whitespace-nowrap">{price.billingPeriod.replace("_", " ")}</td>
                <td className="px-4 py-2 whitespace-nowrap font-medium">{money(price.amount, price.currency)}</td>
                <td className="px-4 py-2">{price.creditAmount ?? "—"}</td>
                <td className="px-4 py-2 whitespace-nowrap">{price.trialDays ? `${price.trialDays} days` : "—"}</td>
                <td className="px-4 py-2">{price.liveSubscribers}</td>
                <td className="px-4 py-2">
                  <Toggle
                    checked={price.isActive}
                    disabled={busy || variant}
                    label={`Price ${price.id} active`}
                    onChange={() => onTogglePrice(price)}
                  />
                </td>
                <td className="px-4 py-2 text-right">
                  {canChangeAmount(plan, price) && (
                    <button type="button" className={linkButtonClass} onClick={() => onModal({ kind: "amount", plan, price })}>
                      Change amount
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {plan.prices.length === 0 && <p className="px-4 py-3 text-sm text-gray-400">No prices yet.</p>}
      </div>
    </div>
  );
};

/** Plans and prices of one product line, including private, switched-off and discounted copies. */
export const PlansTab: React.FC<Readonly<{ productLine: ProductLine }>> = ({ productLine }) => {
  const load = useCallback(() => listPlans(productLine), [productLine]);
  const { data, loading, error, reload } = useLoad(load);
  const [showInactive, setShowInactive] = useState(false);
  const [showVariants, setShowVariants] = useState(false);
  const [modal, setModal] = useState<ModalState>(null);
  const [pendingOff, setPendingOff] = useState<AdminPrice | null>(null);
  const toggle = useAction();

  const plans = useMemo(() => data ?? [], [data]);
  const nameById = useMemo(() => new Map(plans.map((p) => [p.id, p.name])), [plans]);
  const inactiveCount = plans.filter((p) => !p.isActive).length;
  const variantCount = plans.filter(isVariant).length;
  // Each discounted copy sits right under the plan it copies, so turning the
  // filter on visibly changes the list instead of appending at the very end.
  const visible = useMemo(() => {
    const shown = plans.filter((p) => (showInactive || p.isActive) && (showVariants || !isVariant(p)));
    const copiesOf = new Map<number, AdminPlan[]>();
    for (const p of shown) {
      if (p.basePlanId !== null) copiesOf.set(p.basePlanId, [...(copiesOf.get(p.basePlanId) ?? []), p]);
    }
    const placed = new Set<number>();
    const ordered: AdminPlan[] = [];
    for (const p of shown) {
      if (isVariant(p)) continue;
      ordered.push(p);
      for (const copy of copiesOf.get(p.id) ?? []) {
        ordered.push(copy);
        placed.add(copy.id);
      }
    }
    // Copies whose base plan is hidden by the other filter still show.
    return [...ordered, ...shown.filter((p) => isVariant(p) && !placed.has(p.id))];
  }, [plans, showInactive, showVariants]);

  const closeAndReload = () => {
    setModal(null);
    reload();
  };

  const applyToggle = async (price: AdminPrice, isActive: boolean) => {
    const done = await toggle.run(() => setPriceActive(price.id, isActive));
    if (done) {
      setPendingOff(null);
      await reload();
    }
  };

  const onTogglePrice = (price: AdminPrice) => {
    // Switching off a price people are billed on deserves a second look.
    if (price.isActive && price.liveSubscribers > 0) {
      toggle.setError(null);
      setPendingOff(price);
      return;
    }
    applyToggle(price, !price.isActive);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-4">
          <Toggle
            checked={showInactive}
            onChange={setShowInactive}
            label="Show switched-off plans"
            text={`Show switched-off plans (${inactiveCount})`}
          />
          {productLine === "DRAWPOST_ADS" && (
            <Toggle
              checked={showVariants}
              onChange={setShowVariants}
              label="Show offer prices"
              text={`Show offer prices (${variantCount})`}
            />
          )}
          {data && (
            <span className="text-xs text-gray-400">
              Showing {visible.length} of {plans.length} plans
            </span>
          )}
        </div>
        <button type="button" className={primaryButtonClass} onClick={() => setModal({ kind: "create" })}>
          <Plus className="w-4 h-4" /> New plan
        </button>
      </div>

      {toggle.error && !pendingOff && <p className="text-sm text-red-600">{toggle.error}</p>}

      {loading && !data ? (
        <LoadState loading error={null} onRetry={reload} />
      ) : (
        <>
          <LoadState loading={false} error={error} onRetry={reload} />
          {!error && visible.length === 0 && <p className="py-12 text-center text-sm text-gray-400">No plans to show.</p>}
          {visible.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              baseName={plan.basePlanId ? nameById.get(plan.basePlanId) ?? null : null}
              productLine={productLine}
              busy={toggle.busy}
              onModal={setModal}
              onTogglePrice={onTogglePrice}
            />
          ))}
        </>
      )}

      {modal?.kind === "create" && <CreatePlanModal productLine={productLine} onClose={() => setModal(null)} onSaved={closeAndReload} />}
      {modal?.kind === "edit" && <EditPlanModal plan={modal.plan} onClose={() => setModal(null)} onSaved={closeAndReload} />}
      {modal?.kind === "addPrice" && <AddPriceModal plan={modal.plan} onClose={() => setModal(null)} onSaved={closeAndReload} />}
      {modal?.kind === "amount" && (
        <ChangeAmountModal plan={modal.plan} price={modal.price} onClose={() => setModal(null)} onSaved={closeAndReload} />
      )}
      {modal?.kind === "settings" && <PlanSettingsModal plan={modal.plan} onClose={() => setModal(null)} onSaved={closeAndReload} />}

      {pendingOff && (
        <ConfirmModal
          title="Switch this price off?"
          tone="danger"
          confirmLabel="Switch off"
          busy={toggle.busy}
          error={toggle.error}
          onCancel={() => setPendingOff(null)}
          onConfirm={() => applyToggle(pendingOff, false)}
          message={
            <p>
              {pendingOff.liveSubscribers} subscriber{pendingOff.liveSubscribers === 1 ? " is" : "s are"} on{" "}
              {money(pendingOff.amount, pendingOff.currency)} / {pendingOff.billingPeriod}. They keep their subscription;
              the price just disappears for new buyers.
            </p>
          }
        />
      )}
    </div>
  );
};
